// ============================================================
// Standortmonitor — Library — Helpers
// Stable data-slice signature for deduplication
// (analogous to `sigOf` in the Premium prototype).
// ============================================================

import { DashboardUtils } from 'src/app/dashboards/DashboardUtils';
import { Granularity } from 'src/app/dashboards/shared/dashboard-config/dashboard-config.model';
import {
  KPI_BY_ID,
  KPI_FILTER_NAME,
  KpiId,
  kpisOfMetric,
} from 'src/app/dashboards/shared/dashboard-config/kpis/kpi-options';
import {
  INDEX_INDICATOR_BY_ID,
  INDICATOR_FILTER_NAME,
  LEGACY_INDICATOR_FILTER_NAMES,
} from 'src/app/dashboards/shared/dashboard-config/kpis/index-indicators';
import { legacyKpiFilterLabels } from 'src/app/dashboards/shared/dashboard-config/kpis/header-words';
import {
  DOSSIER_DEVELOPMENT_SHELL_ID,
  DOSSIER_MARKET_SHELL_ID,
  DOSSIER_ONION_SHELL_ID,
  DOSSIER_OVERVIEW_SHELL_ID,
} from 'src/app/dashboards/shared/analyses/dossiers/_shared/dossier-shell.profile';
import { COMPARISON_FOUR_GRID_SHELL_ID } from 'src/app/dashboards/shared/analyses/comparison/_shared/dashboard-comparison-four-grid/dashboard-comparison-four-grid-shell.profile';
import { COMPARISON_EMPLOYED_DISTRICTS_SHELL_ID } from 'src/app/dashboards/shared/analyses/comparison/employed/db4x5x4-comparison-employed-districts/db4x5x4-comparsion-employed-districts.definition';
import { FORECAST_COMPARISON_SHELL_ID } from 'src/app/dashboards/shared/analyses/forecasts/_shared/dashboard-forecast-comparison/dashboard-forecast-comparison-shell.profile';
import { FORECAST_STRUCTURE_SHELL_ID } from 'src/app/dashboards/shared/analyses/forecasts/_shared/dashboard-forecast-structure/dashboard-forecast-structure-shell.profile';
import { RANKING_MULTI_COLUMN_SHELL_ID } from 'src/app/dashboards/shared/analyses/rankings/_shared/dashboard-ranking-multi-column/dashboard-ranking-multi-column-shell.profile';
import { TIME_SERIES_BAR_SHELL_ID } from 'src/app/dashboards/shared/analyses/time-series/_shared/dashboard-time-series-bar/dashboard-time-series-bar-shell.profile';
import { DataSlice } from './library.model';

/**
 * Persisted four-grid comparison quadrant ids (sub_market / state), in
 * dashboard grid order. Not a real dashboard filter — ignored on restore.
 */
export const COMPARISON_GRIDS_KEY = 'comparison_grids';

/**
 * Row stamp written when merging four-grid controller chart data for library
 * save. Prefer this over `sub_market_id`/`state_id` when present — those keys
 * can be missing on rows that still pass the grid filter applicator.
 */
export const COMPARISON_GRID_ROW_KEY = '__comparison_grid_id';

/** Sentinel code for the aggregated “Übrige” Sankey collector node. */
export const SLICE_SANKEY_OTHERS_CODE = '_others';
export const SLICE_SANKEY_SOURCE_CODE = '_source';

/** Compact number formatting for library preview charts. */
export function formatSlicePreviewValue(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })}M`;
  }
  if (abs >= 1_000) {
    return `${(value / 1_000).toLocaleString('de-DE', { maximumFractionDigits: abs >= 10_000 ? 0 : 1 })}k`;
  }
  return value.toLocaleString('de-DE', { maximumFractionDigits: value % 1 === 0 ? 0 : 1 });
}

/**
 * Granularity used for library preview series axis, scope labels, and ranking-row naming.
 * Market dossier shell ranks Teilmärkte despite States in the dashboard id.
 */
export function resolveSlicePreviewGranularity(dashboardId: string, shellId?: string): Granularity {
  if (shellId === DOSSIER_MARKET_SHELL_ID) {
    return Granularity.SubMarkets;
  }
  try {
    return DashboardUtils.decodeDashboardId(dashboardId).granularityId as Granularity;
  } catch {
    return Granularity.Unknown;
  }
}

/** Single-select scope filter per dashboard granularity (companion to the multiselect at that level). */
const GRANULARITY_SCOPE_FILTER_KEYS: Partial<Record<Granularity, readonly string[]>> = {
  [Granularity.States]: ['sub_market'],
  [Granularity.Districts]: ['sub_market'],
  [Granularity.SubMarkets]: ['state'],
  [Granularity.IndustrySubMarkets]: ['state'],
  /** Ranking × WzClasses multi-column: states multiselect + sub_market companion.
   * marktvergleich-wz-klassen / forecast-structure teilmarktstruktur (5x*x12) need a state
   * single-select to display state label on the card title.
   */
  [Granularity.WzClasses]: ['state', 'sub_market'],
};

const LEGACY_FILTER_LABELS = legacyKpiFilterLabels();
const LEGACY_INDEX_FILTER_LABELS = Object.fromEntries(
  LEGACY_INDICATOR_FILTER_NAMES.map((name) => [name, LEGACY_FILTER_LABELS[name]]),
);
const LEGACY_TYPE_FILTER_LABELS = Object.fromEntries(
  Object.entries(LEGACY_FILTER_LABELS).filter(([name]) => !LEGACY_INDICATOR_FILTER_NAMES.includes(name)),
);

function labelFromFilterMaps(
  filterValues: Record<string, unknown>,
  maps: Record<string, Map<string, string>>,
): string {
  for (const [key, values] of Object.entries(maps)) {
    const raw = filterValues[key];
    if (raw == null) {
      continue;
    }
    const label = values.get(`${raw}`);
    if (label) {
      return label;
    }
  }
  return '';
}

/**
 * Label of the slice's selected metric filter (or empty if the dashboard
 * has no such filter). Used as the card title.
 * Prefers IndexValue (*-index / “pro …”) filters over plain metric filters.
 */
export function resolveSliceMetricFilterLabel(
  slice: Pick<DataSlice, 'filterValues'>,
): string {
  const filterValues = slice.filterValues ?? {};
  return (
    INDEX_INDICATOR_BY_ID[`${filterValues[INDICATOR_FILTER_NAME]}`]?.label ||
    // Slices saved before the indicator and kpi filters: legacy index, then legacy type filters.
    labelFromFilterMaps(filterValues, LEGACY_INDEX_FILTER_LABELS) ||
    kpiFilterLabel(filterValues) ||
    labelFromFilterMaps(filterValues, LEGACY_TYPE_FILTER_LABELS)
  );
}

/** Label of the `kpi` filter value; empty for single-KPI metrics, which have no type choice. */
function kpiFilterLabel(filterValues: Record<string, unknown>): string {
  const kpi = KPI_BY_ID[filterValues[KPI_FILTER_NAME] as KpiId];
  return kpi && kpisOfMetric(kpi.metricId).length > 1 ? kpi.label : '';
}

type ScopeFilterLabelResolvers = Partial<
  Record<'state' | 'sub_market' | 'wz_class', (id: string | number) => string | undefined>
>;

function formatScopeFilterLabel(raw: unknown, resolve?: (id: string | number) => string | undefined): string {
  if (raw == null || raw === '') {
    return '';
  }
  if (Array.isArray(raw)) {
    return raw
      .map((id) => formatScopeFilterLabel(id, resolve))
      .filter(Boolean)
      .join(' · ');
  }
  const label = resolve?.(raw as string | number) ?? `${raw}`;
  return label.replace(/[Σ∑]/g, '').trim();
}

/** Resolve Teilmarkt label for forecast cards (never WZ-Klasse). */
function resolveForecastSubMarketScopeLabel(
  slice: DataSlice,
  resolvers: ScopeFilterLabelResolvers,
): string {
  const raw = slice.filterValues?.['sub_market'];
  if (raw != null && raw !== '') {
    const mapped = resolvers.sub_market?.(Number(raw));
    if (mapped) {
      return mapped.replace(/[Σ∑]/g, '').trim();
    }
  }
  return '';
}

/**
 * Forecast-comparison card scope — the single-selects that are *not* the
 * compared axis:
 * - bundesvergleich-wz-klassen: WZ-Klasse (states are compared)
 * - bundesvergleich-teilmarkte: Teilmarkt
 * - marktvergleich-*: Teilmarkt in Bundesland (e.g. Design in Sachsen)
 */
function resolveForecastComparisonScopeLabel(
  slice: DataSlice,
  resolvers: ScopeFilterLabelResolvers,
): string {
  const state = formatScopeFilterLabel(slice.filterValues?.['state'], resolvers.state);

  const wzClass = formatScopeFilterLabel(
    slice.filterValues?.['wz_class'],
    resolvers.wz_class,
  );
  if (wzClass) {
    return state ? `${wzClass} in ${state}` : wzClass;
  }

  const subMarket = resolveForecastSubMarketScopeLabel(slice, resolvers);
  if (subMarket && state) {
    return `${subMarket} in ${state}`;
  }
  return subMarket || state;
}

/**
 * `{Teilmarkt|WZ-Klasse} in {Bundesland}` from whichever single-selects the
 * shell keeps; falls back to just one side when the other is a multiselect.
 * Used by forecast-stack (Branchenstruktur has neither, so state only) and
 * time-series bar.
 */
function resolveMarketInStateScopeLabel(
  slice: DataSlice,
  resolvers: ScopeFilterLabelResolvers,
): string {
  const state = formatScopeFilterLabel(slice.filterValues?.['state'], resolvers.state);
  const wzClass = formatScopeFilterLabel(
    slice.filterValues?.['wz_class'],
    resolvers.wz_class,
  );
  const subject = wzClass || resolveForecastSubMarketScopeLabel(slice, resolvers);

  if (subject && state) {
    return `${subject} in ${state}`;
  }
  return subject || state;
}

/**
 * `Berlin + Potsdam` → `Berlin/Potsdam` (duel side districts).
 * Bare `A` / `B` means the side resolved to no district — not a label.
 */
function formatDuelSideLabel(raw: string): string {
  if (raw === 'A' || raw === 'B') {
    return '';
  }
  return raw
    .split('+')
    .map((part) => part.trim())
    .filter(Boolean)
    .join('/');
}

/**
 * Duel card scope: `Berlin/Potsdam vs. München` — the two captured A/B sides,
 * which the preview series already carries as resolved district names.
 */
function resolveComparisonDuelScopeLabel(slice: DataSlice): string {
  const [sideA, sideB] = (slice.previewSeries ?? []).map((entry) =>
    formatDuelSideLabel(entry.name ?? ''),
  );
  if (sideA && sideB) {
    return `${sideA} vs. ${sideB}`;
  }
  return sideA || sideB || '';
}

/**
 * Human-readable label(s) for the slice's scope filter(s), keyed by
 * effective preview granularity (companion single-select at that level).
 * single-select displayed as title and multi.
 */
export function resolveSliceScopeFilterLabel(
  slice: DataSlice,
  resolvers: ScopeFilterLabelResolvers,
): string {
  // Dossier overview / Entwicklung: state is the card scope (· Sachsen).
  if (
    slice.shellId === DOSSIER_OVERVIEW_SHELL_ID ||
    slice.shellId === DOSSIER_DEVELOPMENT_SHELL_ID
  ) {
    return formatScopeFilterLabel(slice.filterValues['state'], resolvers.state);
  }

  // Onion: "Design in Sachsen" (sub_market in state).
  if (slice.shellId === DOSSIER_ONION_SHELL_ID) {
    const subMarket = formatScopeFilterLabel(
      slice.filterValues['sub_market'],
      resolvers.sub_market,
    );
    const state = formatScopeFilterLabel(slice.filterValues['state'], resolvers.state);
    if (subMarket && state) {
      return `${subMarket} in ${state}`;
    }
    return subMarket || state;
  }

  // Duel: "Berlin/Potsdam vs. München" (A/B district sides).
  if (slice.shellId === COMPARISON_EMPLOYED_DISTRICTS_SHELL_ID) {
    return resolveComparisonDuelScopeLabel(slice);
  }

  // Forecast comparison (incl. legacy slices identified by dashboardVariant).
  if (slice.shellId === FORECAST_COMPARISON_SHELL_ID) {
    return resolveForecastComparisonScopeLabel(slice, resolvers);
  }

  // Forecast stack / Entwicklung: "{Design} in {Sachsen}" from single-selects.
  if (
    slice.shellId === FORECAST_STRUCTURE_SHELL_ID ||
    slice.shellId === TIME_SERIES_BAR_SHELL_ID
  ) {
    return resolveMarketInStateScopeLabel(slice, resolvers);
  }

  // WZ multi-column ranking: title scope is sub_market only (· Design).
  if (slice.shellId === RANKING_MULTI_COLUMN_SHELL_ID) {
    return formatScopeFilterLabel(slice.filterValues['sub_market'], resolvers.sub_market);
  }

  // Four-grid / quad-line: title is the compare mode only (no · scope).
  if (slice.shellId === COMPARISON_FOUR_GRID_SHELL_ID) {
    return '';
  }

  const granularityId = resolveSlicePreviewGranularity(slice.dashboardId, slice.shellId);
  const filterKeys = GRANULARITY_SCOPE_FILTER_KEYS[granularityId] ?? [];
  const filterValues = slice.filterValues ?? {};

  const parts: string[] = [];

  const wzClassLabel = formatScopeFilterLabel(filterValues['wz_class'], resolvers.wz_class);
  if (wzClassLabel) {
    parts.push(wzClassLabel);

    if (!filterKeys.includes('state')) {
      const stateLabel = formatScopeFilterLabel(filterValues['state'], resolvers.state);
      if (stateLabel) {
        parts.push(stateLabel);
      }
    }
  }

  for (const key of filterKeys) {
    const label = formatScopeFilterLabel(
      filterValues[key],
      resolvers[key as keyof ScopeFilterLabelResolvers],
    );
    if (label) {
      parts.push(label);
    }
  }

  return parts.join(' · ');
}

/** Part of a slice that defines its identity. */
export type SliceSignatureInput = Pick<
  DataSlice,
  'dashboardId' | 'dashboardVariant' | 'datasetName' | 'filterValues' | 'live'
>;

/** Serialize filter values deterministically (sorted keys, sorted arrays). */
function normalizeFilterValues(filterValues: Record<string, unknown>): string {
  return Object.keys(filterValues)
    .sort()
    .map((key) => {
      const value = filterValues[key];
      const normalized = Array.isArray(value)
        ? [...value].map((v) => `${v}`).sort().join(',')
        : `${value}`;
      return `${key}=${normalized}`;
    })
    .join('|');
}

/**
 * Build a stable key from dashboard, dataset, and filter values.
 * Two views with the same recipe produce the same signature.
 */
export function sliceSignature(slice: SliceSignatureInput): string {
  return [
    slice.dashboardId,
    slice.dashboardVariant ?? '',
    slice.datasetName,
    slice.live ? 'live' : 'snapshot',
    normalizeFilterValues(slice.filterValues ?? {}),
  ].join('::');
}
